'use client'

import {useState, useRef, useEffect} from 'react'
import Image from 'next/image'
import {useIntl} from 'react-intl'

type HeaderSearchProps = {
  action?: string
  /**
   * JO2-246, new header layout: search is an icon at every width that opens
   * the search field. Off (default): today's behaviour, an icon below `lg` and
   * an always-open field from `lg`.
   */
  compact?: boolean
}

type SearchMode = 'compact' | 'default'

// Opening swaps the icon button for the search box, so each one animates as it
// appears: the box fades in and its icon slides a little to the left into
// place; on closing, the icon fades back in sliding a little to the right.
// Both are off for visitors who ask for reduced motion.
const OPEN_FORM_ANIMATION = 'animate-in fade-in-0 duration-200 motion-reduce:animate-none'
const OPEN_ICON_ANIMATED =
  'size-4 shrink-0 animate-in fade-in-0 slide-in-from-right-2 duration-200 motion-reduce:animate-none'
const CLOSED_ICON_ANIMATED =
  'size-4 shrink-0 animate-in fade-in-0 slide-in-from-left-2 duration-200 motion-reduce:animate-none'

// Full literal class names, so Tailwind keeps them (no interpolation).
// compact (the icon toggle, the default): the closed toggle is 24px wide
// (w-6), not 45px: wide enough to tap (WCAG 2.2 target size). The icon is
// 16px, so -mr-1 lets the tap area overlap the gap by 4px and the visible gap
// to the next icon matches the others in the row. Height stays 45px.
// default (headerSearchStatic): today's phone toggle, unchanged.
// Focus shows as a full-contrast ring on the rounded box (the input has no
// ring of its own); outline-hidden keeps an outline in forced-colours mode.
const TOGGLE_CLASS: Record<SearchMode, string> = {
  compact:
    '-mr-1 flex h-[45px] w-6 cursor-pointer items-center justify-center rounded-full border border-transparent bg-transparent transition-all duration-300 ease-in-out',
  default:
    'flex h-[45px] w-[45px] cursor-pointer items-center justify-center rounded-full border border-transparent bg-transparent transition-all duration-300 ease-in-out lg:hidden',
}

const OPEN_FORM_CLASS: Record<SearchMode, string> = {
  compact:
    'flex h-[45px] w-48 items-center gap-2 rounded-full border border-[#4B4F54] bg-card pl-site-xs pr-site-xs transition-all duration-300 ease-in-out focus-within:ring-2 focus-within:ring-foreground focus-within:ring-offset-2 lg:w-64',
  default:
    'flex h-[45px] w-48 items-center gap-2 rounded-full border border-[#4B4F54] bg-card pl-site-xs pr-site-xs transition-all duration-300 ease-in-out focus-within:ring-2 focus-within:ring-foreground focus-within:ring-offset-2 lg:hidden',
}

// Below this width the inline expanding search input collides with the hanging
// logo tag, so the search icon instead opens the mega-menu (which has its own
// search field). Boundary matches the Tailwind `max-[440px]` variant — 440px+
// keeps the inline expand. See Header logo tab + MegaMenu search input.
const TINY_SCREEN_QUERY = '(max-width: 439.98px)'

// Open the mega-menu sheet and move focus into its search input. Returns false
// when the menu trigger isn't in the DOM so the caller can fall back to the
// inline expand. Polls for the input because the Base UI sheet mounts its
// content on open; focusing an element inside the dialog survives the sheet's
// own focus trap.
function openMenuWithSearch(): boolean {
  const trigger = document.getElementById('mega-menu-sheet-trigger')
  if (!trigger) return false
  trigger.click()
  let attempts = 0
  const focusSearch = () => {
    const input = document.getElementById('mega-menu-search') as HTMLInputElement | null
    if (input) {
      input.focus()
    } else if (attempts++ < 40) {
      requestAnimationFrame(focusSearch)
    }
  }
  requestAnimationFrame(focusSearch)
  return true
}

export function HeaderSearch({action = '/search', compact = false}: HeaderSearchProps) {
  const mode: SearchMode = compact ? 'compact' : 'default'
  const intl = useIntl()
  const t = (id: string, defaultMessage: string) => intl.formatMessage({id, defaultMessage})

  const searchLabel = t('navigation.search.label', 'Search')
  const searchPlaceholder = `${searchLabel}...`
  const searchSubmit = t('navigation.search.submit', 'Search')

  const [open, setOpen] = useState(false)
  // Set on the first open, so the closed icon does not animate on page load.
  const [toggled, setToggled] = useState(false)
  const containerRef = useRef<HTMLFormElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const toggleRef = useRef<HTMLButtonElement>(null)
  // Escape closes the field and unmounts it, so focus would fall to <body>.
  // Return it to the toggle. A click outside keeps focus where it landed.
  const returnFocus = useRef(false)

  useEffect(() => {
    if (open) inputRef.current?.focus()
    else if (returnFocus.current) {
      returnFocus.current = false
      toggleRef.current?.focus()
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        returnFocus.current = true
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  return (
    <>
      {/* Mobile: toggle between button (closed) and input (open) */}
      {!open ? (
        <button
          ref={toggleRef}
          type="button"
          aria-label={searchLabel}
          onClick={() => {
            if (typeof window !== 'undefined' && window.matchMedia(TINY_SCREEN_QUERY).matches) {
              if (openMenuWithSearch()) return
            }
            setToggled(true)
            setOpen(true)
          }}
          className={TOGGLE_CLASS[mode]}
        >
          <Image
            src="/icons/search.svg"
            alt=""
            width={16}
            height={16}
            sizes="16px"
            className={toggled ? CLOSED_ICON_ANIMATED : 'size-4 shrink-0'}
          />
        </button>
      ) : (
        <form
          action={action}
          method="get"
          ref={containerRef}
          className={`${OPEN_FORM_CLASS[mode]} ${OPEN_FORM_ANIMATION}`}
        >
          <label htmlFor="header-search-mobile" className="sr-only">
            {searchLabel}
          </label>
          {/* No focus ring of its own: the rounded box shows focus for the
              whole control (focus-within), so the field does not get a second
              border inside it. */}
          <input
            ref={inputRef}
            id="header-search-mobile"
            name="q"
            type="text"
            placeholder={searchPlaceholder}
            className="min-w-0 flex-1 bg-transparent text-base text-foreground placeholder:text-[#4B4F54] outline-hidden"
          />
          <button type="submit" aria-label={searchSubmit}>
            <Image
              src="/icons/search.svg"
              alt=""
              width={16}
              height={16}
              sizes="16px"
              className={OPEN_ICON_ANIMATED}
            />
          </button>
        </form>
      )}

      {/* Desktop: always visible expanded input (not in the compact layout) */}
      {compact ? null : (
        <form
          action={action}
          method="get"
          className="hidden h-[45px] items-center gap-2 rounded-full border border-[#4B4F54] bg-card pl-site-xs pr-site-xs focus-within:ring-2 focus-within:ring-foreground focus-within:ring-offset-2 lg:flex"
        >
          <label htmlFor="header-search-desktop" className="sr-only">
            {searchLabel}
          </label>
          <input
            id="header-search-desktop"
            name="q"
            type="text"
            placeholder={searchPlaceholder}
            className="min-w-0 flex-1 bg-transparent text-base text-foreground placeholder:text-[#4B4F54] outline-hidden"
          />
          <button type="submit" aria-label={searchSubmit}>
            <Image
              src="/icons/search.svg"
              alt=""
              width={16}
              height={16}
              sizes="16px"
              className="size-4 shrink-0"
            />
          </button>
        </form>
      )}
    </>
  )
}
